// Capacity helpers shared by the Pace view and staffing scenarios.
//
// A person's capacity in a month is their working hours: weekdays less company close
// dates, less their own time off, scaled by FTE. It is always worked out one month at a
// time. Pooling several months would let a vacation in one month be covered by spare
// time in another, which is exactly the mistake a multi-month plan must not make.

export const HOURS_PER_DAY = 8;

const pad2 = (n) => String(n).padStart(2, '0');
export const toIsoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

// Working days of a month as 'YYYY-MM-DD' strings. Close dates are stored as recurring
// month/day pairs; one that lands on a weekend never removed a working day, so it is
// ignored (the same rule as the forecast page).
export const buildWorkingDays = (monthStartIso, closeDates) => {
  const [y, m] = String(monthStartIso).split('-').map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m)) return [];

  const closed = new Set();
  for (const cd of Array.isArray(closeDates) ? closeDates : []) {
    const match = String(cd?.closeDate ?? cd?.close_date ?? '').slice(0, 10).match(/^\d{4}-(\d{2})-(\d{2})$/);
    if (match && Number(match[1]) === m) closed.add(Number(match[2]));
  }

  const days = [];
  const daysInMonth = new Date(y, m, 0).getDate();
  for (let day = 1; day <= daysInMonth; day += 1) {
    const d = new Date(y, m - 1, day);
    const dow = d.getDay();
    if (dow === 0 || dow === 6 || closed.has(day)) continue;
    days.push(toIsoDay(d));
  }
  return days;
};

// The time off endpoint answers with a day -> hours map, or a list of entries on older
// payloads. Both are normalized to { 'YYYY-MM-DD': hours }.
export const buildTimeOffHoursByDay = (payload) => {
  const byDay = {};
  const add = (rawDay, rawHours) => {
    const day = String(rawDay || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
    const hours = Number(rawHours);
    byDay[day] = Number.isFinite(hours) && hours >= 0 ? hours : HOURS_PER_DAY;
  };

  if (payload?.time_off_hours_by_day && typeof payload.time_off_hours_by_day === 'object') {
    for (const [day, hours] of Object.entries(payload.time_off_hours_by_day)) add(day, hours);
  } else if (Array.isArray(payload?.entries)) {
    for (const e of payload.entries) add(e?.date ?? e?.time_off_date, e?.hours);
  }
  return byDay;
};

// { capacity, timeOff } for one person in one month, given that month's working days.
export const monthCapacity = (workingDays, timeOffByDay, fteScale = 1) => {
  let capacity = 0;
  let timeOff = 0;
  for (const iso of workingDays) {
    const off = Math.min(HOURS_PER_DAY, Math.max(0, Number(timeOffByDay?.[iso] ?? 0)));
    timeOff += off;
    capacity += (HOURS_PER_DAY - off) * fteScale;
  }
  return { capacity, timeOff };
};

export const fteScaleOf = (user) => {
  const raw = Number(user?.ftePercent ?? user?.fte_percent ?? 100);
  return Number.isFinite(raw) ? Math.max(0, raw) / 100 : 1;
};
